import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useUiStore } from '../../stores/ui.store';
import { useLogout } from '../../hooks/useAuth';
import { portal } from '../../App';
import i18n from '../../i18n/index';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'zh', label: '中文' },
  { code: 'th', label: 'ไทย' },
  { code: 'vi', label: 'VI' },
];

// --- Retail nav tabs (legacy-style top nav) --------------------------------
const RETAIL_NAV: NavItem[] = [
  { to: '/pos', label: 'POS', icon: 'fa-home' },
  { to: '/session', label: 'Session', icon: 'fa-clock-o' },
  { to: '/inventory', label: 'Inventory', icon: 'fa-cubes' },
  { to: '/customers', label: 'Customers', icon: 'fa-users' },
  { to: '/invoices', label: 'Invoices', icon: 'fa-file-text-o' },
  { to: '/tables', label: 'Tables', icon: 'fa-th-large' },
  { to: '/vault', label: 'Vault', icon: 'fa-university' },
];

// --- Manager nav tabs ------------------------------------------------------
const MANAGER_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: 'fa-dashboard' },
  { to: '/reports', label: 'Reports', icon: 'fa-bar-chart' },
  { to: '/inventory', label: 'Inventory', icon: 'fa-cubes' },
  { to: '/customers', label: 'Customers', icon: 'fa-users' },
  { to: '/invoices', label: 'Invoices', icon: 'fa-file-text-o' },
  { to: '/settings', label: 'Settings', icon: 'fa-cog' },
  { to: '/users', label: 'Users', icon: 'fa-user' },
  { to: '/suppliers', label: 'Suppliers', icon: 'fa-industry' },
];

// --- Vault nav tabs --------------------------------------------------------
const VAULT_NAV: NavItem[] = [
  { to: '/vault', label: 'Vault', icon: 'fa-university' },
  { to: '/invoices', label: 'Invoices', icon: 'fa-file-text-o' },
];

// --- Distributor nav tabs --------------------------------------------------
const DISTRIBUTOR_NAV: NavItem[] = [
  { to: '/inventory', label: 'Inventory', icon: 'fa-cubes' },
  { to: '/suppliers', label: 'Suppliers', icon: 'fa-industry' },
  { to: '/customers', label: 'Customers', icon: 'fa-users' },
  { to: '/invoices', label: 'Invoices', icon: 'fa-file-text-o' },
];

function getNavItems(): NavItem[] {
  switch (portal) {
    case 'manager': return MANAGER_NAV;
    case 'vault': return VAULT_NAV;
    case 'distributor': return DISTRIBUTOR_NAV;
    default: return RETAIL_NAV;
  }
}

const PORTAL_LABEL: Record<string, string> = {
  retail: 'Motra POS',
  manager: 'Manager',
  vault: 'Vault',
  distributor: 'Distributor',
};

export default function TopNav() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const user = useAuthStore((s) => s.user);
  const { language, setLanguage } = useUiStore();
  const logoutMutation = useLogout();
  const navItems = getNavItems();

  function handleLanguageChange(code: string) {
    setLanguage(code);
    i18n.changeLanguage(code);
  }

  return (
    <header className="topnav">
      <div className="topnav__brand">
        <span className="topnav__logo">{PORTAL_LABEL[portal] ?? 'Motra POS'}</span>
        {shop?.name && <span className="topnav__shop">{shop.name}</span>}
      </div>

      <nav className="topnav__tabs">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `topnav__tab${isActive ? ' topnav__tab--active' : ''}`
            }
            end={item.to === '/pos' || item.to === '/dashboard' || item.to === '/vault'}
          >
            <i className={`fa ${item.icon}`} />
            <span>{t(item.label, item.label)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="topnav__right">
        <div className="topnav__lang">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`topnav__lang-btn${language === lang.code ? ' topnav__lang-btn--active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>
        <span className="topnav__user">
          <i className="fa fa-user-circle-o" /> {user?.username}
        </span>
        <button
          className="topnav__logout"
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
        >
          <i className="fa fa-sign-out" /> {t('logout', 'Logout')}
        </button>
      </div>
    </header>
  );
}
